/**
 * RetroLanding — StudySync landing, rebuilt with ONLY the vendored RetroUI
 * components + Tailwind (ZERO @mui/* in this tree).
 *
 * Section flow (all matched to the brutalist reference, adapted to a LIGHT page +
 * StudySync brand — Cardinal #8C1515 / Teal #00505C / #FAFAFA, black borders +
 * hard offset shadows):
 *   1. Nav            — sticky brutalist top bar
 *   2. Hero           — headline + dual CTAs
 *   3. Trusted-by     — dark Teal band, big static institution logos
 *   4. KPI band       — by-the-numbers (real public stats)
 *   5. Features       — "everything a study needs" grid
 *   6. Setup          — "how it works" 3-step flow + Health-app screenshot
 *   7. Devices        — forward-deployed 3D device showcase + "tell us about your study"
 *   8. Researcher     — "see every device, live" dashboard (dark)
 *   9. Studies map    — where studies are running
 *  10. Final CTA      — "ready to run a better study?" + Schedule-a-demo dialog
 *  11. Footer         — dark Teal, brand / index / social
 *
 * The page alternates light surfaces with the dark Teal band / dashboard / footer.
 */
import * as React from 'react';
import {
  LandingNav,
  HeroSection,
  TrustedBySection,
  KpiBandSection,
  FeaturesSection,
  SetupSection,
  ForwardDeploySection,
  ResearcherDashboardSection,
  StudiesMapSection,
  FinalCtaSection,
  LandingFooter,
} from './index';
import { ContactDialogProvider } from './ContactDialogProvider';

const RetroLanding: React.FC = () => {
  // Disable page-level overscroll (rubber-band bounce + scroll-chaining) while the
  // landing is mounted. The document root (html) is the scroll container, so set it
  // there; restore the prior value on unmount so other routes are unaffected.
  React.useEffect(() => {
    const html = document.documentElement;
    const prev = html.style.overscrollBehavior;
    html.style.overscrollBehavior = 'none';
    return () => {
      html.style.overscrollBehavior = prev;
    };
  }, []);

  return (
    // ContactDialogProvider owns the single shared contact dialog opened by both the
    // hero "Talk to us" CTA and the final "Schedule a demo" CTA.
    <ContactDialogProvider>
      <div className="retroui-root min-h-screen overflow-x-clip bg-background font-sans text-foreground antialiased">
      <LandingNav />
      {/* Desktop (lg+): the hero + Trusted-by band together fill the first viewport
          (minus the 88px sticky nav) so BOTH are visible before any scroll, dynamically
          at any window height. The hero flex-grows to absorb leftover height and centers
          its content; the band sits at the bottom edge, and the next section begins right
          at the fold. Below lg this is a normal stacked flow. */}
      <div className="lg:flex lg:min-h-[calc(100vh_-_88px)] lg:flex-col">
        <HeroSection />
        <TrustedBySection />
      </div>
      <KpiBandSection />
      <FeaturesSection />
      <SetupSection />
      <ForwardDeploySection />
      <ResearcherDashboardSection />
      <StudiesMapSection />
      <FinalCtaSection />
      <LandingFooter />
      </div>
    </ContactDialogProvider>
  );
};

export default RetroLanding;
